import { useEffect, useState } from "react";
import { NavLink, Route, Routes, Navigate } from "react-router-dom";
import { SignOutButton, UserButton } from "@clerk/react";
import { Activity, BarChart3, Building2, CalendarDays, ClipboardCheck, FileText, Inbox, Languages, LayoutDashboard, Megaphone, Menu, Mic, PhoneCall, Pill, Presentation, Printer, Rocket, Settings as Cog, Smartphone, Users, X } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { CatalogProvider } from "@/components/admin/kit";
import { LogoMark, SignalBars } from "@/components/site/TopBar";
import Dashboard from "./Dashboard";
import Hospitals from "./Hospitals";
import Onboarding from "./Onboarding";
import Billing, { AdminAudit, OrgReports } from "./Billing";
import Patients, { PatientDetail } from "./Patients";
import ScriptBuilder from "./ScriptBuilder";
import Simulator from "./Simulator";
import CallSchedule from "./CallSchedule";
import UsersPage, { ViewAs } from "./Users";
import CallLog from "./CallLog";
import HealthUpdates from "./HealthUpdates";
import Templates from "./Templates";
import DemoRequests from "./DemoRequests";
import Settings from "./Settings";
import WebsiteClips from "./WebsiteClips";
import MedicinesHub from "./MedicinesHub";
import DemoTools from "./DemoTools";
import GoLive from "./GoLive";
import Printables from "./Printables";
import { Queue } from "@/components/subs/Queue";
import { CaseView } from "@/components/subs/CaseView";

const SubsQueue = () => <Queue api={useApi()} base="/admin/submissions" />;
const SubsCase = () => <CaseView api={useApi()} base="/admin/submissions" />;

const SECTIONS = [
  ["Daily work", [
    ["", "Dashboard", LayoutDashboard, Dashboard],
    ["submissions", "Submissions", ClipboardCheck, SubsQueue],
    ["patients", "Patients", Users, Patients],
    ["call-plan", "Call plan", CalendarDays, CallSchedule],
    ["call-log", "Call log", PhoneCall, CallLog],
  ]],
  ["Organisations", [
    ["hospitals", "Organisations", Building2, Hospitals],
    ["onboarding", "Onboarding", Building2, Onboarding],
    ["users", "Users & roles", Users, UsersPage],
  ]],
  ["Money", [["billing", "Usage & billing", BarChart3, Billing]]],
  ["Reports", [
    ["monthly-reports", "Monthly reports", BarChart3, OrgReports],
    ["audit", "Audit log", FileText, AdminAudit],
  ]],
  ["Demo & go-live", [
    ["demo", "Demo tools", Presentation, DemoTools],
    ["printables", "Printable materials", Printer, Printables],
    ["go-live", "Before going live", Rocket, GoLive],
  ]],
  ["Setup", [
    ["medicine-list", "Medicine list", Pill, MedicinesHub],
    ["templates", "Templates & languages", Languages, Templates],
    ["scripts", "Script builder", FileText, ScriptBuilder],
    ["simulator", "Call simulator", Smartphone, Simulator],
    ["website-clips", "Website voice clips", Mic, WebsiteClips],
    ["health-updates", "Health updates", Megaphone, HealthUpdates],
    ["demo-requests", "Demo requests", Inbox, DemoRequests],
    ["settings", "Settings", Cog, Settings],
  ]],
];
const NAV = SECTIONS.flatMap(([, items]) => items);

const DemoBanner = () => (
  <div className="demo-stripe no-print sticky top-0 z-40 flex items-center justify-center gap-2 px-3 py-1.5 text-center text-xs font-bold tracking-wide text-white" data-testid="demo-mode-banner">
    <Activity size={14} /> DEMO MODE — demo data only. Records marked “REAL – TEST ONLY” are your family test schedule.
  </div>
);

function Sidebar({ onNav }) {
  return (
    <nav className="grid gap-0.5 p-3" aria-label="Admin">
      {SECTIONS.map(([title, items]) => (
        <div key={title} className="mb-2" data-testid={`admin-nav-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-[var(--ink)]/45">{title}</p>
          {items.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/admin/${path}`} end={path === ""} onClick={onNav} data-testid={`admin-nav-${path || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[var(--violet)] text-white" : "text-[var(--ink)]/75 hover:bg-[var(--lav-2)]"}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Shell() {
  const [open, setOpen] = useState(false);
  return (
    <CatalogProvider>
      <div className="min-h-screen bg-[var(--lav)]">
        <DemoBanner />
        <header className="no-print sticky top-[28px] z-30 flex items-center gap-3 border-b border-[var(--lav-3)] bg-white/85 px-4 py-2.5 backdrop-blur">
          <button className="grid h-9 w-9 place-items-center rounded-lg lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-menu-btn"><Menu size={20} /></button>
          <a href="/admin" className="flex items-center gap-2 font-bold text-[var(--ink)]" data-testid="admin-brand-logo">
            <LogoMark size={32} /> <span>ondose<SignalBars /></span> <span className="hidden text-xs font-medium text-muted-foreground sm:inline">admin</span>
          </a>
          <NavLink to="/admin/demo" className="ml-auto rounded-full bg-[var(--violet)] px-3 py-1.5 text-xs font-bold text-white" data-testid="admin-header-demo">Demo</NavLink>
          <a href="/" className="text-xs font-medium text-[var(--violet)] hover:underline" data-testid="admin-view-site">View website</a>
          <UserButton />
        </header>
        <div className="flex">
          <aside className="no-print sticky top-[84px] hidden h-[calc(100vh-84px)] w-64 shrink-0 overflow-y-auto border-r border-[var(--lav-3)] bg-white lg:block"><Sidebar /></aside>
          {open && (
            <div className="fixed inset-0 z-50 bg-black/40 lg:hidden" onClick={() => setOpen(false)}>
              <aside className="h-full w-72 overflow-y-auto bg-white" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end p-2"><button onClick={() => setOpen(false)} aria-label="Close menu" className="p-2"><X size={18} /></button></div>
                <Sidebar onNav={() => setOpen(false)} />
              </aside>
            </div>
          )}
          <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
            <Routes>
              {NAV.map(([path, , , C]) => <Route key={path} path={path || "/"} element={<C />} />)}
              <Route path="submissions/case/:id" element={<SubsCase />} />
              <Route path="patients/:id" element={<PatientDetail />} />
              <Route path="scan" element={<Navigate to="/admin/submissions" replace />} />
              <Route path="test-call" element={<Navigate to="/admin/demo" replace />} />
              <Route path="reports" element={<Navigate to="/admin/monthly-reports" replace />} />
              <Route path="medicines" element={<Navigate to="/admin/medicine-list?tab=patients" replace />} />
              <Route path="pronunciations" element={<Navigate to="/admin/medicine-list?tab=pronunciations" replace />} />
              <Route path="view-as/:orgId/*" element={<ViewAs />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </CatalogProvider>
  );
}

export default function AdminApp() {
  const api = useApi();
  const [state, setState] = useState("checking");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/me").then(() => setState("ok")).catch((e) => { setMsg(errMsg(e)); setState("denied"); });
  }, [api]);
  if (state === "checking") return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Checking access…</div>;
  if (state === "denied")
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--lav)] p-6" data-testid="admin-denied">
        <div className="max-w-sm rounded-3xl bg-white p-8 text-center">
          <h1 className="text-xl font-bold">Not authorised</h1>
          <p className="mt-2 text-sm text-muted-foreground">{msg}. Only the OnDose admin email can open this area.</p>
          <SignOutButton><button className="btn-orange mt-5 rounded-xl px-5 py-2.5 font-semibold" data-testid="admin-denied-signout">Sign out</button></SignOutButton>
        </div>
      </div>
    );
  return <Shell />;
}
